import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from "react";
import { deleteFile, getFilesByUserId } from "../../api/filesApi";
import Sidebar from '../Sidebar/Sidebar';
import Footer from '../Footer/Footer';
import { ArrowLeft, Trash2 } from 'lucide-react';
import ShareModal from './ShareModal';
import { sendEmailWithFile } from '../../api/emailApi';

export default function CategoryView() {
    const [sidebarExpanded, setSidebarExpanded] = useState(true);
    const { category } = useParams();
    const navigate = useNavigate();
    const [files, setFiles] = useState([]);
    const decodedCategory = decodeURIComponent(category);
    const userId = localStorage.getItem('userId');
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [selectedFileToShare, setSelectedFileToShare] = useState(null);

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                const data = await getFilesByUserId(userId);
                setFiles(data[decodedCategory] || []);
            } catch (err) {
                console.error("Error fetching files for category:", err);
            }
        };

        fetchFiles();
    }, [decodedCategory]);

    const handleDelete = async (fileId) => {
        try {
            await deleteFile(fileId);
            setFiles((prev) => prev.filter(file => file.id !== fileId));
        } catch (err) {
            console.error("Error deleting file:", err);
        }
    };

    const handleShare = (file) => {
        setSelectedFileToShare(file);
        setShareModalOpen(true);
    };

    const handleShareSubmit = async ({ recipientEmail, message, file }) => {
        try {
            await sendEmailWithFile(userId, file.id, recipientEmail, message);
            alert("File shared successfully!");
            setShareModalOpen(false);
        } catch (err) {
            console.error("Error sharing file:", err);
            alert("An error occurred while sharing the file. Please try again.");
        }
    };

    return (
        <div className={`ml-0 flex flex-col min-h-screen transition-all duration-300 ${sidebarExpanded ? 'md:ml-56' : 'md:ml-16'}`}>
            <Sidebar expanded={sidebarExpanded} setExpanded={setSidebarExpanded} />

            <main className="flex-1 p-6 overflow-auto bg-gray-100">
                <section className="space-y-6">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/results')}
                        className="flex items-center text-[#04395b] hover:text-[#022b42] text-sm font-medium mb-2"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Results
                    </button>

                    <h2 className="text-3xl font-semibold text-[#04395b]">{decodedCategory}</h2>

                    {files.length === 0 ? (
                        <p className="text-gray-600">No files in this category.</p>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {files.map((file) => (
                                <div
                                    key={file.id}
                                    className="flex flex-col justify-between p-4 bg-white border rounded shadow-sm relative"
                                >
                                    <button
                                        onClick={() => handleDelete(file.id)}
                                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                                        title="Delete file"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>

                                    <div className="mb-2">
                                        <p className="font-medium text-[#04395b] break-all">{file.originalname}</p>
                                        <p className="text-xs text-gray-500">{new Date(file.uploaded_at).toLocaleDateString()}</p>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => window.open(file.file_url, '_blank')}
                                            className="text-blue-600 hover:text-blue-800 text-sm mr-2"
                                        >
                                            Download
                                        </button>
                                        <button
                                            onClick={() => handleShare(file)}
                                            className="text-green-600 hover:text-green-800 text-sm"
                                        >
                                            Share
                                        </button>
                                    </div>
                                </div>
                            ))}

                        </div>
                    )}
                </section>
            </main>

            <ShareModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                onSubmit={handleShareSubmit}
                file={selectedFileToShare}
            />

            <Footer />
        </div>
    );
}
